/**
 * Transports between the SDK client and an engine host.
 *
 * * `WorkerTransport` (browser default): the engine runs in a module Web Worker.
 * * `InlineTransport`: the engine runs in the calling thread (Node, tests, tools).
 *   Messages are still delivered asynchronously, so client code behaves the same.
 */
import { EngineHost } from './host.js';
export class WorkerTransport {
    worker;
    crashCb = null;
    constructor(workerUrl) {
        // The literal `new URL(..., import.meta.url)` lets bundlers emit the worker.
        this.worker =
            workerUrl === undefined
                ? new Worker(new URL('./worker.js', import.meta.url), { type: 'module', name: 'dotloom-engine' })
                : new Worker(workerUrl, { type: 'module', name: 'dotloom-engine' });
        this.worker.addEventListener('error', (ev) => {
            ev.preventDefault();
            this.crashCb?.(ev.message || 'worker error');
        });
        this.worker.addEventListener('messageerror', () => this.crashCb?.('message could not be deserialized'));
    }
    send(msg, transfer) {
        this.worker.postMessage(msg, transfer ?? []);
    }
    onMessage(cb) {
        this.worker.addEventListener('message', (ev) => cb(ev.data));
    }
    onCrash(cb) {
        this.crashCb = cb;
    }
    terminate() {
        this.worker.terminate();
    }
}
export class InlineTransport {
    host;
    cb = null;
    closed = false;
    constructor(factory, stepBudget) {
        this.host = new EngineHost((msg) => {
            // Deliver asynchronously, like postMessage.
            queueMicrotask(() => {
                if (!this.closed)
                    this.cb?.(msg);
            });
        }, factory, stepBudget);
    }
    send(msg) {
        // Structured-clone semantics are not needed in-thread; dispatch on a microtask.
        queueMicrotask(() => {
            if (!this.closed)
                this.host.receive(msg);
        });
    }
    onMessage(cb) {
        this.cb = cb;
    }
    onCrash(_cb) {
        // In-thread crashes surface as `crashed` protocol messages.
    }
    terminate() {
        this.closed = true;
    }
}
//# sourceMappingURL=transport.js.map